import { DARK_ART_IDS } from './darkArtIds.js';

// Terugval voor de donkere illustraties (UX-ronde 2). art-dark.css wisselt het plaatje met
// `content: url()`; een browser die dat op een <img> niet doet (Firefox) hield in het donker de
// lichte illustratie, een felle vlek op het donkere scherm. Hier wisselt de app daarom zelf de src
// (/art/illus/x.svg <-> /art-dark/illus/x.svg) zodra de weergave donker is, ook voor plaatjes die
// later verschijnen. Eén MutationObserver, alleen actief in het donker; zelfde herkomst, dus de CSP
// blijft gelijk. In Chrome en Safari geeft dit hetzelfde beeld als de CSS (de CSS werkt al vóór
// dit script, dus geen flits).

const LIGHT = /\/art\/illus\/([a-z0-9-]+)\.svg$/;
const DARK = /\/art-dark\/illus\/([a-z0-9-]+)\.svg$/;

/** De src die bij deze weergave hoort, of null als er niets te wisselen is. */
export function swappedSrc(src: string, dark: boolean): string | null {
  if (dark) {
    const m = LIGHT.exec(src);
    if (!m || !DARK_ART_IDS.has(m[1]!)) return null;
    return src.replace(LIGHT, `/art-dark/illus/${m[1]!}.svg`);
  }
  const m = DARK.exec(src);
  if (!m) return null;
  return src.replace(DARK, `/art/illus/${m[1]!}.svg`);
}

function swapImg(img: HTMLImageElement, dark: boolean): void {
  const src = img.getAttribute('src');
  if (!src) return;
  const next = swappedSrc(src, dark);
  if (next !== null) img.setAttribute('src', next);
}

function swapAll(root: ParentNode, dark: boolean): void {
  const imgs = root.querySelectorAll<HTMLImageElement>('img[src*="/illus/"]');
  for (let i = 0; i < imgs.length; i++) swapImg(imgs[i]!, dark);
}

/**
 * Houdt de illustraties gelijk aan html[data-theme] (zie state/colorScheme.ts). Geeft een
 * opruimfunctie terug.
 */
export function installDarkArtSwap(html: HTMLElement = document.documentElement): () => void {
  if (typeof MutationObserver !== 'function') return () => undefined;
  const isDark = () => html.getAttribute('data-theme') === 'dark';
  let dark = isDark();
  const content = new MutationObserver((records) => {
    if (!dark) return;
    for (const r of records) {
      if (r.type === 'attributes') {
        if (r.target instanceof HTMLImageElement) swapImg(r.target, true);
        continue;
      }
      r.addedNodes.forEach((n) => {
        if (n instanceof HTMLImageElement) swapImg(n, true);
        else if (n instanceof Element) swapAll(n, true);
      });
    }
  });
  const watch = () => {
    content.disconnect();
    if (dark) {
      content.observe(html, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['src'],
      });
    }
  };
  const theme = new MutationObserver(() => {
    const next = isDark();
    if (next === dark) return;
    dark = next;
    swapAll(html, dark);
    watch();
  });
  theme.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
  if (dark) swapAll(html, true);
  watch();
  return () => {
    theme.disconnect();
    content.disconnect();
  };
}
